import Badge from "@/components/base/Badge";
import { cn } from "@/utils/cn";
import { formatDate, formatFcfa } from "@/utils/format";
import type { Formule, FormuleId } from "../types";

interface FormuleCardProps {
  formule: Formule;
  current: boolean;
  /** Formule choisie mais dont le paiement n'est pas encore validé. */
  paiementRequis?: boolean;
  /** Date limite de validation du paiement (affiche si paiement requis). */
  dateLimitePaiement?: string;
  /** Une demande de paiement est déjà en attente de validation. */
  paiementEnAttente?: boolean;
  onChoose: (id: FormuleId) => void;
}

function formatLimite(valeur: number | null, base: string): string {
  if (valeur === null) return `${base}s illimités`;
  return `${valeur} ${base}${valeur > 1 ? "s" : ""}`;
}

export default function FormuleCard({ formule, current, paiementRequis = false, dateLimitePaiement = "", paiementEnAttente = false, onChoose }: FormuleCardProps) {
  const gratuit = formule.prixMensuel === 0;
  const items = [
    formatLimite(formule.limites.immeubles, "immeuble"),
    formatLimite(formule.limites.biens, "bien"),
    formatLimite(formule.limites.locataires, "locataire"),
    formatLimite(formule.limites.contrats, "contrat"),
    formatLimite(formule.limites.utilisateurs, "utilisateur"),
  ];

  return (
    <article
      className={cn(
        "card-lift flex h-full flex-col rounded-lg border bg-background-50 p-5 md:p-6",
        current
          ? "border-primary-400 bg-primary-50/40"
          : paiementRequis
            ? "border-accent-400 bg-accent-50/40"
            : "border-background-200 hover:border-primary-300"
      )}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-heading text-base font-semibold text-foreground-950">{formule.nom}</h3>
        {paiementRequis ? (
          <Badge tone="accent" icon="ri-time-line">
            Paiement requis
          </Badge>
        ) : current ? (
          <Badge tone="primary" icon="ri-checkbox-circle-line">
            Actuelle
          </Badge>
        ) : (
          formule.highlight && <Badge tone="accent">Populaire</Badge>
        )}
      </div>
      <p className="mt-1 text-sm text-foreground-600">{formule.tagline}</p>

      {paiementRequis && dateLimitePaiement && (
        <p className="mt-3 flex items-start gap-2 rounded-md border border-accent-200 bg-accent-50/70 px-3 py-2 text-xs text-accent-900">
          <i className="ri-timer-line mt-0.5 text-sm" aria-hidden="true" />
          <span>
            Date limite de validation : <strong>{formatDate(dateLimitePaiement)}</strong>
          </span>
        </p>
      )}

      <div className="mt-4">
        <p className="font-heading text-2xl font-semibold text-foreground-950">
          {gratuit ? "0 FCFA" : formatFcfa(formule.prixMensuel)}
          {!gratuit && <span className="ml-1 font-body text-sm text-foreground-500">/ mois</span>}
        </p>
        <p className="mt-0.5 text-xs text-foreground-500">
          {gratuit ? "Gratuit à vie" : `${formatFcfa(formule.prixAnnuel)} / an`}
        </p>
      </div>

      <ul className="mt-5 flex flex-1 flex-col gap-2.5 border-t border-background-200 pt-4">
        {items.map((item) => (
          <li key={item} className="flex items-start gap-2 text-sm text-foreground-700">
            <i className="ri-check-line mt-0.5 text-secondary-600" aria-hidden="true" />
            <span>{item}</span>
          </li>
        ))}
      </ul>

      <button
        type="button"
        onClick={() => onChoose(formule.id)}
        disabled={current || paiementEnAttente}
        className={cn(
          "mt-5 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md px-5 py-2.5 text-sm font-label font-medium transition-colors",
          current
            ? "cursor-default border border-primary-300 bg-primary-100 text-primary-900"
            : paiementEnAttente
              ? "cursor-not-allowed border border-accent-300 bg-accent-50 text-accent-800"
              : "cursor-pointer bg-primary-500 text-background-50 hover:bg-primary-600"
        )}
      >
        {current ? (
          <>
            <i className="ri-check-line text-base" aria-hidden="true" />
            Formule actuelle
          </>
        ) : paiementEnAttente ? (
          <>
            <i className="ri-time-line text-base" aria-hidden="true" />
            Paiement en attente
          </>
        ) : paiementRequis ? (
          <>
            <i className="ri-secure-payment-line text-base" aria-hidden="true" />
            Payer cette formule
          </>
        ) : (
          <>
            <i className="ri-exchange-line text-base" aria-hidden="true" />
            Changer de formule
          </>
        )}
      </button>
    </article>
  );
}